import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import s from './AccessResult.module.css';

const ROLE_NAMES: Record<string, string> = {
  ADMIN: '管理员', SALES: '业务员', FOLLOWER: '跟单员',
  WAREHOUSE: '仓库员', PURCHASE: '采购员', VIEWER: '查看员',
};

function orderScope(perms: string[]) {
  if (perms.includes('orders.read.all')) return '全部订单';
  const own = perms.includes('orders.read.own');
  const assigned = perms.includes('orders.read.assigned');
  if (own && assigned) return '本人创建及分配给我';
  if (own) return '本人创建';
  if (assigned) return '分配给我';
  return '无订单范围';
}

function ResultFrame({ title, back, children }: { title: string; back: { to: string; label: string }; children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <header className={s.header}>
        <Link to={back.to}><Icon name="back" size={22} />{back.label}</Link>
        <b>{title}</b>
      </header>
      <div className={s.body}>{children}</div>
    </div>
  );
}

export function ForbiddenModule({ moduleName, description, permissionName, permissionCode }: {
  moduleName: string;
  description: string;
  permissionName: string;
  permissionCode: string;
}) {
  const user = useSession((st) => st.user);
  const [copyState, setCopyState] = useState('');
  const role = user?.roleName || ROLE_NAMES[user?.role || ''] || user?.role || '—';
  const scope = orderScope(user?.permissions || []);
  const copy = async () => {
    const message = `${user?.name || user?.username || '当前账号'}（${user?.username || '—'}）申请开通「${permissionName}」权限（${permissionCode}），链接：${window.location.href}`;
    try {
      await navigator.clipboard.writeText(message);
      setCopyState('已复制申请说明，可以发给管理员。');
    } catch {
      setCopyState('复制失败，请检查浏览器的剪贴板权限。');
    }
  };

  return (
    <ResultFrame title={moduleName} back={{ to: '/apps', label: '应用' }}>
      <div className={s.result}>
        <div className={s.icon}><Icon name="lock" size={24} /></div>
        <h1>你还没有「{moduleName}」的权限</h1>
        <p>{description}需要「{permissionName}」权限，你当前的角色没有这一项。</p>
        <dl className={s.details}>
          <div><dt>当前账号</dt><dd>{user?.name || '—'} · {user?.username || '—'}</dd></div>
          <div><dt>角色</dt><dd>{role} · 数据范围：{scope}</dd></div>
          <div><dt>缺少权限</dt><dd className="tnum">{permissionCode}</dd></div>
        </dl>
        <div className={s.buttons}>
          <button type="button" className={s.primary} onClick={copy}><Icon name="copy" />复制申请说明，发给管理员</button>
          <Link to="/" className={s.secondary}>回到主屏</Link>
        </div>
        {copyState && <p className={s.feedback} role="status">{copyState}</p>}
      </div>
    </ResultFrame>
  );
}

export function MissingOrder() {
  const { setPhoneTabBarPinned } = useOutletContext<{ setPhoneTabBarPinned?: (visible: boolean) => void }>();
  useEffect(() => {
    setPhoneTabBarPinned?.(true);
    return () => setPhoneTabBarPinned?.(false);
  }, [setPhoneTabBarPinned]);
  return (
    <ResultFrame title="订单详情" back={{ to: '/orders', label: '订单' }}>
      <div className={s.result}>
        <div className={s.icon}><Icon name="srch" size={24} /></div>
        <h1>找不到这张订单</h1>
        <p>这张订单不存在，或不在你的可见范围内。</p>
        <div className={s.advice}>如果是同事发来的链接，请对方确认订单号；需要看别人名下的订单，请管理员调整你的数据范围。</div>
        <div className={s.buttons}>
          <Link to="/orders?search=1" className={s.primary}><Icon name="srch" />搜索其他订单</Link>
          <Link to="/orders" className={s.secondary}>回到订单列表</Link>
        </div>
      </div>
    </ResultFrame>
  );
}
